"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, Calendar, ShieldCheck, TrendingUp, UserCheck, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
const trustPoints = ["Direct Consultant Access – You work directly with me", "Tailored Local & Regional Strategy for Peterborough Businesses", "Transparent Reporting & Practical Implementation", "Honest Measurement with No AI Templates or Agency Middlemen"];
const highlightStats = [{
  label: "Local Visibility Boost",
  value: "3.4x",
  sub: "average Google Map Pack gain"
}, {
  label: "Direct Expertise",
  value: "100%",
  sub: "no junior account managers"
}, {
  label: "SEO Focus",
  value: "10+",
  sub: "years of organic search strategy"
}];
export default function Localseohero() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".hero-animate", {
        y: 24
      });
      gsap.to(".hero-animate", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power3.out"
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".hero-animate", {
        opacity: 1,
        y: 0
      });
    });
  }, {
    scope: containerRef
  });
  return <section id="localseohero" data-nav="dark" ref={containerRef} className="relative min-h-[85vh] bg-primary text-primary-foreground overflow-hidden py-16 md:py-24 lg:py-28 flex items-center">
      {/* Decorative ambient elements */}
      <div className="absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 size-[500px] rounded-full bg-secondary/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Column */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            
            {/* Pill Badge */}
            <div className="hero-animate" style={{
            opacity: 0
          }}>
              <Badge variant="secondary" className="bg-secondary/90 text-secondary-foreground font-medium px-3.5 py-1.5 text-xs sm:text-sm rounded-full flex items-center gap-2 shadow-xs">
                <MapPin className="size-3.5 text-accent shrink-0" />
                <span>Peterborough &amp; Cambridgeshire Local SEO Specialist</span>
              </Badge>
            </div>

            {/* H1 Heading */}
            <h2 className="hero-animate font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.12]" style={{
            opacity: 0
          }}>
              Local SEO Consultant in Peterborough for Sustainable Search Growth
            </h2>

            {/* Sub-headline paragraph */}
            <p className="hero-animate font-sans text-base sm:text-lg md:text-xl text-primary-foreground/90 max-w-2xl font-normal leading-relaxed" style={{
            opacity: 0
          }}>
              I am Yassine Essaadi, an independent freelance SEO consultant. I help Peterborough and UK regional businesses dominate local search rankings, attract high-intent local customers, and build lasting organic authority on Google Map Pack and search results.
            </p>

            {/* Bullet Trust Points */}
            <div className="hero-animate w-full space-y-3 pt-2" style={{
            opacity: 0
          }}>
              {trustPoints.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-primary-foreground/95 font-medium">
                    {point}
                  </span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="hero-animate flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4 w-full sm:w-auto" style={{
            opacity: 0
          }}>
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 py-6 text-base rounded-lg shadow-md transition-all hover:translate-y-[-1px]">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-5 mr-2" /></SafeHydrate>
                  Book a Free SEO Consultation
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground font-medium px-6 py-6 text-base rounded-lg shadow-xs">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>

            {/* Supporting Micro-copy */}
            <div className="hero-animate flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/80 pt-1" style={{
            opacity: 0
          }}>
              <ShieldCheck className="size-4 text-accent" />
              <span>Free 30-minute strategic review. No sales pitch, no obligation.</span>
            </div>

          </div>

          {/* Right Column: Expert Profile & Trust Card */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="hero-animate relative w-full max-w-md bg-card text-card-foreground rounded-2xl p-6 sm:p-8 shadow-2xl border border-border/60" style={{
            opacity: 0
          }}>
              {/* Profile Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative size-20 rounded-full overflow-hidden border-2 border-primary/20 shrink-0 bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant in Peterborough" loading="lazy" decoding="async" className="size-full object-cover" />
                </div>
                <div>
                  <h2 className="font-serif text-xl font-bold text-foreground">
                    Yassine Essaadi
                  </h2>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <UserCheck className="size-4 text-primary" />
                    Independent SEO Specialist
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Serving Peterborough &amp; Nationwide
                  </p>
                </div>
              </div>

              {/* Personal Commitment Quote */}
              <div className="bg-muted/60 rounded-xl p-4 mb-6 border border-border/40">
                <p className="text-xs sm:text-sm text-foreground/90 italic leading-relaxed">
                  {"\"When you hire me for local SEO in Peterborough, you deal directly with me from day one. I conduct every audit, build every custom roadmap, and refine every search signal myself.\""}
                </p>
              </div>

              {/* Stat Highlights */}
              <div className="grid grid-cols-3 gap-2 py-4 border-t border-b border-border/60 text-center">
                {highlightStats.map((stat, idx) => <div key={idx} data-index={idx} className="px-1">
                    <p className="font-serif text-xl sm:text-2xl font-bold text-primary">
                      {stat.value}
                    </p>
                    <p className="text-[13px] font-semibold text-foreground mt-0.5">
                      {stat.label}
                    </p>
                  </div>)}
              </div>

              {/* Local SEO Signal Checks */}
              <div className="mt-6 space-y-2.5 text-xs sm:text-sm">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <Search className="size-4 text-primary" />
                    Google Business Profile
                  </span>
                  <span className="font-semibold text-foreground">Optimised</span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <MapPin className="size-4 text-primary" />
                    Local Citation Network
                  </span>
                  <span className="font-semibold text-foreground">Verified</span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="size-4 text-primary" />
                    Local Search Intent
                  </span>
                  <span className="font-semibold text-foreground">Targeted</span>
                </div>
              </div>

              {/* Action Button inside card */}
              <div className="mt-6 pt-2">
                <Button asChild variant="default" size="default" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg text-sm shadow-xs">
                  <Link to="/LocalSEOPage">
                    View Local SEO Services
                  </Link>
                </Button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}